import { observer } from "mobx-react";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { ButtonIcon } from "../../../../components/inputs/button-icon/button-icon";
import { useCallback, useEffect, useState } from "react";
import GridLineLayout from "../../../../components/layouts/grid-line-layout/grid-line-layout";
import './email-type-editor.scss';
import IconTest2 from "../../../../components/icons/icon-test2/icon-test2";
import CurrentPortalStore from "../../../../stores/current-portal-store";
import useTranslation from "../../../../translation/hooks/translation-hook";
import emailCustomizationStore from "../../stores/email-customization-store";
import { NotificationTestModal } from "../notification-test-modal/notification-test-modal";
import emailContentListStore from "../../stores/email-content-list-store";
import { NotificationModal } from "../../../../components/modal/components/notification-modal/notification-modal";
import NotificationEventStore from "../../stores/notification-event-store";
import { EmailRichTextEditor } from "../email-rich-text-editor/email-rich-text-editor";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../../components/tooltip/tooltip";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../components/fonts/font-props";
import IconTranslationIndicator from "../../../../components/icons/icon-translation-indicator/icon-translation-indicator";
import IconVariablesAndEvents from "../../../../components/icons/icon-variables-and-events/icon-variables-and-events";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import { useParams } from "react-router-dom";
import TranslateInputSubject from "../translate-input-subject/translate-input-subject";
import TranslationScope from "../../../../translation/translation-scope";
import { trackTranslationValidationOpened, TranslationEntryPoint, TranslationSurface } from "../../../../translation/metrics/translations-mixpanel";

const EmailTypeEditor: React.FC = observer(() => {
    const params = useParams();
    const { selectedLocale } = useTranslation();
    const eventId = params.eventId;
    const [contentEditMode, setContentEditMode] = useState<boolean>(false);
    const [footerEditMode, setFooterEditMode] = useState<boolean>(false);
    const [notifyMessage, setNotifyMessage] = useState('');
    const [showNotifySuccess, setShowNotifySuccess] = useState(false);
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const [testContinue, setTestContinue] = useState<boolean>(false);
    const [testNotification, setTestNotification] = useState<NotificationEventStore>();
    const { setSubTitles } = usePage();

    const emailContentStore = emailContentListStore.selectedEvent;
    const notificationTemplateStore = emailContentStore?.notificationTemplatesStore;
    const title = emailContentStore?.title;
    const scope = emailContentStore?.scope;
    const hasChanges = () => notificationTemplateStore?.hasChanges() ?? false;

    const { retryNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    useEffect(() => {
        emailContentListStore.setSelectedEventId(eventId);

        return () => {
            emailContentListStore.setSelectedEventId(undefined);
        }
    }, [eventId]);

    useEffect(() => {
        notificationTemplateStore?.setSelectedLocale(selectedLocale);
        emailContentListStore.onShowTranslationButton(false);
        notificationTemplateStore?.init(selectedLocale)
    }, [selectedLocale, notificationTemplateStore]);

    useEffect(() => setSubTitles(['Emails', 'Content', title]), [title, setSubTitles]);

    useEffect(() => window.scrollTo({ top: 0, behavior: 'auto' }), []);

    const onSaveClick = () => {
        notificationTemplateStore?.save().then(() => {
            emailContentStore?.setNotification(notificationTemplateStore.model);
            setContentEditMode(false);
            setFooterEditMode(false);
        });
    }

    const toggleEditMode = () => {
        contentEditMode && setContentEditMode(false);
        footerEditMode && setFooterEditMode(false);
    };

    const activateContentEditMode = () => {
        setContentEditMode(true);
        setFooterEditMode(false);
    };

    const activateFooterEditMode = () => {
        setFooterEditMode(true);
        setContentEditMode(false);
    };

    const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noreferrer');
    };

    const onSendTestNotificationClicked = () => {
        trackTranslationValidationOpened({
            validation_type: "send_test",
            surface: TranslationSurface.Emails,
            scope: scope as string,
            locale: selectedLocale,
            entry_point: TranslationEntryPoint.ObjectPage,
            has_unsaved_changes: !!notificationTemplateStore?.hasChanges(),
        });
        setTestNotification(emailContentStore);
        setTestContinue(!notificationTemplateStore?.hasChanges());
    }

    if (!scope) {
        /** temporary solution */
        return null;
    }

    return (
        <EditorPageLayout
            toolbar={{
                hasLocalesSelector: true, hasBack: true,
                content: (
                    <div className="email-type-editor-toolbar">
                        <Tooltip content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                Events & variables
                            </FontBody2>
                        } variant={TOOLTIP_VARIANTS.INFO}>
                            <ButtonIcon Component={() => <IconVariablesAndEvents/>}
                                onClick={() => openInNewTab('../variables-and-events')}></ButtonIcon>
                        </Tooltip>
                        <Tooltip content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                Test Email
                            </FontBody2>
                        } variant={TOOLTIP_VARIANTS.INFO}>
                            <ButtonIcon Component={() => <IconTest2/>}
                                onClick={onSendTestNotificationClicked} >
                            </ButtonIcon>
                        </Tooltip>
                    </div>
                ),
                saveButton: {
                    hasChanges: notificationTemplateStore?.hasChanges(),
                    onClick: () => onSaveClick(),
                    disabled: !notificationTemplateStore?.hasChanges(),
                },
                isLoading: notificationTemplateStore?.isSaving()
            }}
        >
            <TranslationScope scope={scope as string} locale={selectedLocale}>
                <TranslateInputSubject emailTemplateStore={notificationTemplateStore} />
            </TranslationScope>

            <GridLineLayout>
                <div onClick={toggleEditMode} className="editor-place-holder"
                    style={{ background: emailCustomizationStore.backgroundColor }}>
                    <div className="email-editor-template">
                        <div className="email-editor-header" style={{ background: emailCustomizationStore.backgroundColor }}>
                            <div style={{ display: 'none' }}>{emailContentListStore.selectedEvent?.notificationTemplatesStore?.getHeader(selectedLocale)}</div>
                            <div style={{ display: 'inline-flex' }}>
                                {
                                    (emailCustomizationStore.logo) ?
                                        <img alt="" src={emailCustomizationStore.logo} style={{ textAlign: 'center', alignSelf: "center", maxWidth: "570px", maxHeight: "90px", display: "inline !important", verticalAlign: "bottom" }} className="mcnRetinaImage" />
                                        :
                                        <h3 style={{ fontSize: "24px", textTransform: "uppercase", color: emailCustomizationStore.textColor, display: "block" }}>{CurrentPortalStore.setting?.storeName ?? ''}</h3>
                                }
                            </div>
                        </div>
                        {/* Content sections */}
                        <EmailRichTextEditor backgroundColor={"#ffffff"} minHeight={300}
                            toolbarOffset={-40} isEditMode={contentEditMode}
                            content={notificationTemplateStore?.getContent(selectedLocale) ?? ''}
                            onChange={(value: string): void => notificationTemplateStore?.setContent(selectedLocale, value)}
                            onEditModeChange={activateContentEditMode} />

                        {/* Footer sections */}
                        <EmailRichTextEditor backgroundColor={emailCustomizationStore.backgroundColor ?? ''}
                            minHeight={100}
                            toolbarOffset={-90} isEditMode={footerEditMode}
                            content={notificationTemplateStore?.getFooter(selectedLocale) ?? ''}
                            onChange={(value: string) => notificationTemplateStore?.setFooter(selectedLocale, value)}
                            onEditModeChange={activateFooterEditMode} />
                    </div>
                </div>
                {(testNotification && testContinue) && <NotificationTestModal
                    title="Test Events and Notifications"
                    selectedServiceCode={testNotification.setting}
                    channels={emailContentListStore.channels}
                    eventName={testNotification.title}
                    setTestNotificationData={(testNotificationData) => {
                        testNotificationData.eventId = testNotification.eventId as string;
                        testNotificationData.locale = selectedLocale;
                        return testNotification.sendTestNotification(testNotificationData);
                    }}
                    sendButton={{
                        title: 'Send',
                        onClick: (message) => {
                            setTestNotification(undefined);
                            setNotifyMessage(message);
                            setShowNotifySuccess(true);
                        }
                    }}
                    discardButton={{
                        title: 'Discard',
                        onClick: () => {
                            setTestNotification(undefined);
                            setNotifyMessage('');
                            setShowNotifySuccess(false);
                        }
                    }}
                />
                }
                {
                    (testNotification && notificationTemplateStore?.hasChanges()) && <ConfirmModal
                        title='Unsaved Changes'
                        text='You have unsaved changes. Click Continue to send test email without unsaved changes or cancel to continue editing and saving changes.'
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setTestNotification(undefined)
                        }}
                        okButton={{
                            title: 'Continue',
                            onClick: () => setTestContinue(true)
                        }} />
                }
                {showNotifySuccess && <NotificationModal
                    title="Test Events and Notifications"
                    text={notifyMessage}
                    okButton={{
                        title: 'Close',
                        onClick: () => setShowNotifySuccess(false)
                    }} />}
                {isShowUnsaved &&
                    <ConfirmModal
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setIsShowUnsaved(false)
                        }}
                        okButton={{
                            title: 'Discard',
                            onClick: () => {
                                notificationTemplateStore?.discardChanges();
                                setIsShowUnsaved(false);
                                retryNavigation();
                            }
                        }}
                    />
                }

            </GridLineLayout>
            <div className='translation-input' style={{ position: 'relative', top: '-520px' }}>
                <div style={{ width: "100%", display: 'flex', flexDirection: 'row-reverse' }}>
                    <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Translatable field</FontBody2>}>
                        <IconTranslationIndicator color={'var(--color-accent-1-50)'} />
                    </Tooltip>
                </div>
            </div>
            <div className='translation-input' style={{ position: 'relative', top: '-238px' }}>
                <div style={{ width: "100%", display: 'flex', flexDirection: 'row-reverse' }}>
                    <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Translatable field</FontBody2>}>
                        <IconTranslationIndicator color={'var(--color-accent-1-50)'} />
                    </Tooltip>
                </div>
            </div>
        </EditorPageLayout>
    )
});

export default EmailTypeEditor;
